import {
    Box,
    Grid,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { iconSize } from 'assets/theme/iconSize';
import { LeadStatusResponse } from '../services/responses/lead-status.response';
import { LeadStatusService } from '../services/lead-status.service';
import { setLoading, useMaterialUIController } from 'context';
import { ITableProps } from '@types_main/itable.props';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';

export function LeadStatusTable(props: ITableProps<LeadStatusResponse>) {
    const { t } = useTranslation();
    const { orderDirection, handleOrderList, data, editAction } = props;
    const [, dispatch] = useMaterialUIController();
    const { showAlert, alertDelete } = useAlert();

    function doDelete(id: string) {
        const service = new LeadStatusService();
        setLoading(dispatch, true);
        service
            .deleteById(id)
            .then(() => {
                showAlert(t('leadStatus.excluirSucesso'), 'success', 2500);
                props.setRefresh(!props.refresh);
            })
            .catch((err) => {
                showAlert(
                    err.response?.data?.errors?.[0]
                        ?? err.response?.data?.message
                        ?? t('leadStatus.excluirErro'),
                    'error',
                    3000
                );
            })
            .finally(() => {
                setLoading(dispatch, false);
            });
    }

    return (
        <Grid>
            <TableContainer
                sx={{
                    marginTop: '15px',
                    maxHeight: '60vh',
                    overflowY: 'auto',
                }}
            >
                <Table size="medium">
                    <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.card' }}>
                        <TableRow hover>
                            <TableCell width="35%">
                                {t('general.nome')}
                                <TableSortLabel
                                    active={true}
                                    direction={orderDirection}
                                    onClick={handleOrderList}
                                ></TableSortLabel>
                            </TableCell>
                            <TableCell width="30%">{t('leadStatus.cor')}</TableCell>
                            <TableCell width="35%">{t('leadStatus.padrao')}</TableCell>
                            <TableCell width="25%" align="center">
                                {t('general.acoes')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.data ? (
                            data?.data?.records.length > 0 ? (
                                [...data.data.records].map((item, index) => (
                                    <TableRow hover key={index}>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: 20,
                                                        height: 20,
                                                        borderRadius: '50%',
                                                        bgcolor: item.color,
                                                        border: '1px solid rgba(255,255,255,0.2)',
                                                    }}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {item.isDefault && (
                                                <Tooltip title={t('leadStatus.statusPadrao')}>
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <>
                                                    <Tooltip title={t('general.editar')}>
                                                        <IconButton size="small" sx={{ p: 0.25 }} onClick={() => editAction(item.id)}>
                                                            <EditIcon
                                                                style={iconSize}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('general.excluir')}>
                                                        <IconButton
                                                            size="small"
                                                            sx={{ p: 0.25 }}
                                                            onClick={() => {
                                                                alertDelete(
                                                                    item.id,
                                                                    doDelete,
                                                                    item.name
                                                                );
                                                            }}
                                                        >
                                                            <DeleteIcon
                                                                sx={iconSize}
                                                                color="error"
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow hover>
                                    <TableCell colSpan={4} align="center">
                                        {t('general.nenhumRegistro')}
                                    </TableCell>
                                </TableRow>
                            )
                        ) : (
                            Array.from({ length: 5 }, (_, index) => index).map((item, index) => (
                                <TableRow hover key={index}>
                                    <TableCell>
                                        <Skeleton height={30} variant="rectangular" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton height={30} variant="rectangular" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton height={30} variant="rectangular" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton height={30} variant="rectangular" />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
}
